import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Edit, Plus, Minus } from 'lucide-react';

const EditableField = ({ 
  value, 
  onSave, 
  type = 'text', 
  placeholder = 'Hacer clic para editar',
  className = '',
  options = [], // Para select
  multiline = false,
  required = false,
  validation = null,
  multipleSelect = false, // Nuevo: para select múltiple
  allOptions = [] // Nuevo: todas las opciones para select múltiple
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text' || type === 'email' || type === 'tel' || type === 'number') {
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    if (required && (Array.isArray(editValue) ? editValue.length === 0 : !String(editValue).trim())) {
      setError('Este campo es requerido');
      return;
    }

    if (validation) {
      const validationError = validation(editValue);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    if (editValue !== value) {
      onSave(editValue);
    }
    
    setIsEditing(false);
    setError('');
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
    setError('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const renderInput = () => {
    const baseInputClasses = "w-full px-3 py-2 bg-white border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm";
    
    if (type === 'select') {
      return (
        <select
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave} // Guardar al perder el foco
          onKeyDown={handleKeyDown}
          className={baseInputClasses}
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (multipleSelect) {
      const selectedOptions = Array.isArray(editValue) ? editValue : [];
      const availableOptions = allOptions.filter(opt => !selectedOptions.includes(opt.value));

      const handleToggleOption = (optionValue) => {
        if (selectedOptions.includes(optionValue)) {
          setEditValue(selectedOptions.filter(val => val !== optionValue));
        } else {
          setEditValue([...selectedOptions, optionValue]);
        }
      };
      
      const handleAddOption = (optionValue) => {
        setEditValue([...selectedOptions, optionValue]);
      }

      const handleRemoveOption = (optionValue) => {
        setEditValue(selectedOptions.filter(val => val !== optionValue));
      }

      return (
        <div className="space-y-2">
            <div className="flex flex-wrap gap-2 p-2 border border-gray-200 rounded-lg bg-gray-50">
                {selectedOptions.length > 0 ? (
                    selectedOptions.map(selectedId => {
                        const option = allOptions.find(o => o.value === selectedId);
                        return option ? (
                            <span 
                                key={option.value} 
                                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium"
                            >
                                {option.label}
                                <button type="button" onClick={() => handleRemoveOption(option.value)} className="text-blue-500 hover:text-blue-700">
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ) : null;
                    })
                ) : (
                    <span className="text-gray-400 text-xs italic">Ninguna seleccionada</span>
                )}
            </div>
            {availableOptions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500 self-center">Añadir:</span>
                    {availableOptions.slice(0,5).map(option => ( // Mostrar solo algunos para no saturar
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => handleAddOption(option.value)}
                            className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs hover:bg-gray-200 transition-colors"
                        >
                            <Plus className="w-3 h-3 inline-block mr-1" />{option.label}
                        </button>
                    ))}
                    {availableOptions.length > 5 && (
                      <span className="text-xs text-gray-400 self-center">... y {availableOptions.length - 5} más</span>
                    )}
                </div>
            )}
            <div className="flex justify-end gap-2 px-1">
                <motion.button
                    type="button"
                    onClick={handleSave}
                    className="p-1 px-2 text-green-600 hover:bg-green-50 rounded flex items-center gap-1"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                >
                    <Check className="w-3 h-3" /> Guardar
                </motion.button>
                <motion.button
                    type="button"
                    onClick={handleCancel}
                    className="p-1 px-2 text-red-600 hover:bg-red-50 rounded flex items-center gap-1"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                >
                    <X className="w-3 h-3" /> Cancelar
                </motion.button>
            </div>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-xs mt-1"
              >
                {error}
              </motion.div>
            )}
        </div>
      );
    }


    if (multiline) {
      return (
        <textarea
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={`${baseInputClasses} resize-none`}
          rows={3}
        />
      );
    }

    return (
      <input
        ref={inputRef}
        type={type}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={baseInputClasses}
      />
    );
  };

  if (isEditing) {
    return (
      <div className="relative w-full">
        {renderInput()}
        
        {(!multipleSelect && error) && ( // Mostrar el error directamente si no es multiselección
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-500 text-xs mt-1"
          >
            {error}
          </motion.div>
        )}
      </div>
    );
  }

  // Renderizado para modo no-edición
  const displayValue = () => {
    if (type === 'select') {
      const selectedOption = options.find(option => option.value === value);
      return selectedOption ? selectedOption.label : placeholder;
    }
    if (multipleSelect && Array.isArray(value)) {
      const selectedLabels = value.map(id => {
        const option = allOptions.find(o => o.value === id);
        return option ? option.label : 'Desconocida';
      });
      return selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder;
    }
    return value || placeholder;
  };

  return (
    <motion.div
      onClick={() => setIsEditing(true)}
      className={`group cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-colors w-full ${className}`}
      whileHover={{ scale: 1.02 }}
    >
      <div className="flex items-center gap-2">
        <span className={`${!value || (Array.isArray(value) && value.length === 0) ? 'text-gray-400 italic' : 'text-gray-700'} flex-1`}>
          {displayValue()}
        </span>
        <Edit className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </motion.div>
  );
};

export default EditableField;